/**
 * Pure, DB-free helpers for rendering a flat list of rows GROUPED under group
 * header rows in the shared `RecordTable` (opt-in `options.groupBy`).
 *
 * Grouping rides on the existing tree mode (`treeRows.ts`): every distinct group
 * value becomes a synthetic header row, and each data row is a child of its
 * group's header. The tree machinery then supplies the expand/collapse caret, the
 * member-count badge, expand-all / collapse-all and the client-side render order.
 *
 * Group order is first-seen order of the input rows (callers pre-sort the rows),
 * unless the caller passes `sortGroups`. Member order within a group preserves
 * input order.
 * (operator-task-fe-recordtable-row-grouping-support-and-latestn)
 */

/** Marker property carried by a synthetic group header row. */
export const GROUP_HEADER_FIELD = "__recordTableGroup";

/** Prefix of a group header row's id, so it never collides with a record id. */
export const GROUP_HEADER_ID_PREFIX = "__group:";

export interface GroupHeaderInfo {
    key: string;
    label: string;
    count: number;
}

export interface GroupHeaderRow {
    id: string;
    [GROUP_HEADER_FIELD]: GroupHeaderInfo;
}

export interface GroupAccessors<T> {
    /** The group a row belongs to. Rows with the same key share one header. */
    getGroupKey: (row: T) => string;
    /** Header text for a group; defaults to the key, or "(none)" for an empty key. */
    getGroupLabel?: (row: T, key: string) => string;
    /** Optional reorder of the groups (default: first-seen order). */
    sortGroups?: (groups: GroupWithMembers<T>[]) => GroupWithMembers<T>[];
}

export interface GroupWithMembers<T> {
    key: string;
    label: string;
    members: T[];
}

export const isGroupHeaderRow = (row: unknown): row is GroupHeaderRow =>
    row != null &&
    typeof row === "object" &&
    GROUP_HEADER_FIELD in (row as Record<string, unknown>);

export const groupHeaderId = (key: string): string => `${GROUP_HEADER_ID_PREFIX}${key}`;

const defaultLabel = (key: string): string => (key === "" ? "(none)" : key);

/** Partition rows into groups (first-seen order, then the optional sort). */
export function partitionIntoGroups<T>(
    rows: readonly T[],
    { getGroupKey, getGroupLabel, sortGroups }: GroupAccessors<T>
): GroupWithMembers<T>[] {
    const groups: GroupWithMembers<T>[] = [];
    const byKey = new Map<string, GroupWithMembers<T>>();
    for (const row of rows) {
        const key = getGroupKey(row) ?? "";
        let group = byKey.get(key);
        if (!group) {
            group = {
                key,
                label: getGroupLabel ? getGroupLabel(row, key) : defaultLabel(key),
                members: [],
            };
            byKey.set(key, group);
            groups.push(group);
        }
        group.members.push(row);
    }
    return sortGroups ? sortGroups(groups) : groups;
}

/**
 * Interleave a synthetic header row before each group's members. The result is
 * the flat input that tree mode assembles into a forest with
 * {@link groupTreeAccessors}.
 */
export function buildGroupedRows<T>(
    rows: readonly T[],
    accessors: GroupAccessors<T>
): Array<T | GroupHeaderRow> {
    const out: Array<T | GroupHeaderRow> = [];
    for (const group of partitionIntoGroups(rows, accessors)) {
        out.push({
            id: groupHeaderId(group.key),
            [GROUP_HEADER_FIELD]: {
                key: group.key,
                label: group.label,
                count: group.members.length,
            },
        });
        out.push(...group.members);
    }
    return out;
}

/** Tree accessors that attach every data row under its group's header row. */
export function groupTreeAccessors<T>(
    getRowId: (row: T) => string,
    getGroupKey: (row: T) => string
) {
    return {
        getRowId: (row: T | GroupHeaderRow): string =>
            isGroupHeaderRow(row) ? row.id : getRowId(row as T),
        getParentId: (row: T | GroupHeaderRow): string | null =>
            isGroupHeaderRow(row) ? null : groupHeaderId(getGroupKey(row as T) ?? ""),
    };
}
